/**
 * Client-Side API Utilities with Circuit Breaker
 * 
 * Purpose: Wraps API calls to backend with circuit breaker and cache fallback
 * 
 * Architecture:
 * - Client → API Routes → PostgreSQL (primary)
 * - Supabase sync happens server-side only
 * - This file provides helpers for calling our custom API routes
 * - NOT used for direct Supabase database queries
 */

import { supabaseCircuitBreaker } from "./circuit-breaker";
import { getCachedCustomer } from "../offline/customer-cache";

/**
 * Search customers via API endpoint with circuit breaker
 * 
 * This calls our backend API which queries PostgreSQL directly.
 * Falls back to IndexedDB cache on failure.
 */
export async function searchCustomerAPI(query: string, deviceToken?: string) {
  return supabaseCircuitBreaker.execute(
    async () => {
      const response = await fetch(`/api/customers/search?query=${encodeURIComponent(query)}`, {
        headers: {
          Accept: "application/json",
          ...(deviceToken && { "X-Device-Token": deviceToken }),
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return response.json();
    },
    // Fallback: try cache if query is a barcode
    async () => {
      // Only fallback for barcode queries, not phone numbers
      if (query.startsWith("R") && query.length === 13) {
        const cached = await getCachedCustomer(query);
        if (cached) {
          return { found: true, customer: cached };
        }
      }
      throw new Error("Customer not found");
    }
  );
}

